import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseLayout, Menu, type MenuWrapperItem } from '@insio/ui-toolkit';
import { navigation } from '../navigation';
import { useAuth, useMasterPermissions } from '@/features/auth';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  canOpenMasterAccountBoardSection,
  masterAccountBoardHomePath,
  MasterAccountPendingDeletionGate,
  useMasterAccountSettingsSections
} from '@/features/master-account-board-shell';
import { queueMenuItem, useMasterAccountQueueSummary } from '@/features/master-account-board';
import { menuLink, menuLinkClassName } from './chrome/menu-link';
import { AccountSwitchConfirm, SessionChrome } from './chrome/SessionChrome';
import { useProfileMenu } from './chrome/use-profile-menu';

interface MasterAccountBoardLayoutProps {
  children: ReactNode;
}

/**
 * Chrome of the master-account board (a master account): the shared session chrome only, with
 * sections following the master roles.
 */
export function MasterAccountBoardLayout({ children }: MasterAccountBoardLayoutProps) {
  const location = useLocation();
  const t = useFeatureTranslation('masterAccountBoardShell.menu');
  const { user } = useAuth();
  const { hasMasterPermission } = useMasterPermissions();

  const canViewClients = canOpenMasterAccountBoardSection('clients', hasMasterPermission);
  const canManageBilling = canOpenMasterAccountBoardSection('billing', hasMasterPermission);
  const canOpenSettings = useMasterAccountSettingsSections().length > 0;
  const queueToDo = useMasterAccountQueueSummary({ enabled: canViewClients });

  const { profileMenuProps, switchPrompt } = useProfileMenu(
    canManageBilling ? navigation.masterAccountBilling() : undefined
  );

  const isActive = (path: string) => location.pathname.startsWith(path);

  const menuItems: MenuWrapperItem[] = [
    ...(canViewClients
      ? [
          menuLink(
            t('clients'),
            'briefcase',
            navigation.accountRelations(),
            isActive(navigation.accountRelations())
          ),
          queueMenuItem({
            count: queueToDo,
            isActive: isActive(navigation.masterAccountQueue()),
            t,
            className: menuLinkClassName
          })
        ]
      : []),
    ...(canOpenSettings
      ? [
          menuLink(
            t('settings'),
            'building',
            navigation.masterAccountSettings(),
            isActive(navigation.masterAccountSettings())
          )
        ]
      : [])
  ];

  return (
    <div className='flex flex-col h-dvh' data-testid='master-account-board-layout'>
      <SessionChrome />
      <AccountSwitchConfirm prompt={switchPrompt} />
      <BaseLayout orientation='horizontal' className='flex-1 min-h-0 h-full'>
        <BaseLayout.Menu>
          <Menu
            {...profileMenuProps}
            variant='masterAccountBoard'
            label={user?.currentMasterAccount?.name}
            testId='master-account-board-menu'
            items={menuItems}
            showLogo={true}
            showProfile={true}
            orientation='horizontal'
            logoHref={masterAccountBoardHomePath(hasMasterPermission) ?? navigation.dashboard()}
          />
        </BaseLayout.Menu>
        <BaseLayout.Content className='flex flex-col min-h-0'>
          <div className='flex min-h-0 flex-1 flex-row overflow-hidden'>
            <div className='min-w-0 flex-1 overflow-hidden'>
              <MasterAccountPendingDeletionGate>{children}</MasterAccountPendingDeletionGate>
            </div>
          </div>
        </BaseLayout.Content>
      </BaseLayout>
    </div>
  );
}

export default MasterAccountBoardLayout;
